import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from 'react';
import { CustomerInfo, CustomerListItem } from '../models/CustomerData';
import { fetchCustomerDetails, fetchUserCustomers } from '../services/customerService';
import logger from '../utils/logger';
import { useAuth } from './AuthContext'; // Assuming you have an AuthContext to know if user is signed in

interface CustomerContextType {
  availableCustomers: CustomerListItem[];
  isLoadingCustomers: boolean;

  selectedCustomer: CustomerInfo | null;
  selectedApiKey: string | null;
  isLoadingDetails: boolean;

  error: string | null;
  selectCustomerById: (customerId: string) => Promise<void>;
  clearSelectedCustomer: () => void;
}

export const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

interface CustomerProviderProps {
  children: ReactNode;
}

export const CustomerProvider: React.FC<CustomerProviderProps> = ({ children }) => {
  const { isSignedIn, user } = useAuth(); // Get user signed-in state
  const [availableCustomers, setAvailableCustomers] = useState<CustomerListItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerInfo | null>(null);
  const [selectedApiKey, setSelectedApiKey] = useState<string | null>(null);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState<boolean>(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const setupApiKey = useCallback((customerId: string): string | null => {
    logger.debug('setupApiKey: availableCustomers:', availableCustomers);
    const customer = availableCustomers.find(c => c.id === customerId);
    const apiKey = customer?.api_keys?.[0] || null;

    if (!apiKey) {
      logger.warn(`setupApiKey: No API key found for customer ${customerId}.`);
      setSelectedApiKey(null); // Ensure state is cleared
      return null; // Return null if not found
    }
    logger.info(`setupApiKey: Using apiKey ${apiKey} for customer ${customerId}`);
    setSelectedApiKey(apiKey);
    return apiKey; // Return the found API key
  }, [availableCustomers]);

  const selectCustomerById = useCallback(async (customerId: string) => {
    if (!customerId) {
      setSelectedCustomer(null);
      setSelectedApiKey(null); // Also clear API key here
      return;
    }
    setIsLoadingDetails(true);
    setError(null);
    try {
      // Call setupApiKey and get the key for immediate use.
      // setupApiKey will also update the selectedApiKey state.
      const currentApiKey = setupApiKey(customerId);

      if (!currentApiKey) {
        // setError should ideally be set here if this is a critical failure path for selection
        throw new Error(`API key could not be determined for customer ${customerId}.`);
      }

      logger.info(`selectCustomerById: Calling fetchCustomerDetails with apiKey ${currentApiKey} for customerId ${customerId}`);
      const customerDetails = await fetchCustomerDetails(currentApiKey, customerId);
      setSelectedCustomer(customerDetails);
    } catch (err: any) {
      logger.error(`selectCustomerById: Failed to fetch details for customer ${customerId}:`, err);
      setError(err.message || `Could not load details for customer ${customerId}.`);
      setSelectedCustomer(null); // Clear selection on error
      setSelectedApiKey(null); // Also clear API key on error
    } finally {
      setIsLoadingDetails(false);
    }
  }, [setupApiKey]); // setupApiKey is the primary dependency.
                        // It closes over availableCustomers, ensuring freshness.

  // Effect 1: Fetch available customers when user signs in/out
  useEffect(() => {
    if (isSignedIn) {
      setIsLoadingCustomers(true);
      setError(null);
      fetchUserCustomers()
        .then(customers => {
          // logger.debug('useEffect[fetchCustomers]: Fetched available customers:', customers);
          setAvailableCustomers(customers);
          // Auto-selection logic moved to a separate effect
        })
        .catch(err => {
          logger.error('useEffect[fetchCustomers]: Failed to fetch available customers:', err);
          setError(err.message || 'Could not load customer list.');
          setAvailableCustomers([]); // Ensure it's an empty array on error
        })
        .finally(() => setIsLoadingCustomers(false));
    } else {
      // Clear customer data if user signs out
      setAvailableCustomers([]);
      setSelectedCustomer(null);
      setSelectedApiKey(null);
      setIsLoadingCustomers(false);
    }
  }, [isSignedIn, user]); // Only depends on auth state for fetching the list

  // Effect 2: Auto-select customer if applicable (e.g., only one customer available)
  useEffect(() => {
    if (isSignedIn && availableCustomers.length === 1 && !selectedCustomer && !isLoadingCustomers && !isLoadingDetails) {
      // Check !isLoadingCustomers to ensure availableCustomers is stable from the fetch
      // Check !isLoadingDetails to prevent re-triggering if details are already being loaded for this auto-selection
      logger.info(`useEffect[autoSelect]: Auto-selecting single customer: ${availableCustomers[0].id}`);
      selectCustomerById(availableCustomers[0].id);
    }
  }, [isSignedIn, availableCustomers, selectedCustomer, selectCustomerById, isLoadingCustomers, isLoadingDetails]);

  const clearSelectedCustomer = useCallback(() => {
    setSelectedCustomer(null);
    setSelectedApiKey(null);
    // Potentially navigate user back to customer selection or home
  }, []);

  return (
    <CustomerContext.Provider value={{ availableCustomers, selectedCustomer, selectedApiKey, isLoadingCustomers, isLoadingDetails, error, selectCustomerById, clearSelectedCustomer }}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = (): CustomerContextType => {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};